import { Column, Columns, Stack, useResponsiveValue } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useErrorSnackbar } from 'shared/hooks/useErrorSnackbar';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import { LimitedExperienceDialog } from 'shared/modules/shipper/LimitedExperienceDialog';
import { useShipperListHasLimitedExperience } from 'shared/modules/shipper/ShipperUtils';
import { LinkButton } from 'shared/routing/Links';
import { emitLoadEvent } from '../../loads/data/LoadEvents';
import { trackOffersEvent } from './data/OffersAnalytics';
import { useAcceptOffer } from './data/OffersAPI';
import { OfferDetailsDTO } from './data/OffersDTO';
import { OffersDeclineDialog } from './OffersDeclineDialog';
import { useFlag } from 'shared/settings/FeatureToggles';

interface AcceptedOfferPageFooterProps {
  load: OfferDetailsDTO['load'];
  isSubhaulingOffer?: boolean | null;
}

export function AcceptedOfferPageFooter({
  load,
  isSubhaulingOffer,
}: AcceptedOfferPageFooterProps) {
  const isSubhauledEnabled = useFlag('ctms_edi_subhaul');
  const isSubhauledOffer = isSubhauledEnabled && isSubhaulingOffer;

  return (
    <Box
      marginLeft="auto"
      marginRight="auto"
      paddingTop="medium"
      paddingBottom="medium"
    >
      <LinkButton
        size="large"
        variant={isSubhauledOffer ? 'neutral' : 'primary'}
        to={`/loads/${load.guid}?utm_medium=load_offer&utm_source=Web+CTMS`}
        data-intercom-target="offer-open-load-button"
      >
        {isSubhauledOffer ? 'Open Load Details' : 'Open Load'}
      </LinkButton>
    </Box>
  );
}

function PlaceBidButton({
  offer,
  disabled,
}: {
  offer: OfferDetailsDTO;
  disabled: boolean;
}) {
  const isMobile = useResponsiveValue(true, false);

  return (
    <Button
      variant="neutral"
      size={isMobile ? 'medium' : 'large'}
      fullWidth={true}
      disabled={disabled}
      onClick={() => {
        trackOffersEvent({ name: 'Carrier Clicked Place Bid on Load Offer' });
        openExternalURL(
          '/loadboard/loads{?current_load_guid,utm_medium,utm_source}',
          {
            current_load_guid: offer.load.shipper_order_guid,
            utm_source: 'Web CTMS',
            utm_medium: 'Load Offer',
            target: '_blank',
          },
        );
      }}
    >
      Counter Bid
    </Button>
  );
}

interface PendingOfferPageFooterProps {
  offer: OfferDetailsDTO;
}

export function PendingOfferPageFooter({ offer }: PendingOfferPageFooterProps) {
  const isSubahulEnabled = useFlag('ctms_edi_subhaul');
  const isNewShipperAlertEnabled = useFlag('new_shipper_alert');
  const [dialog, setDialog] = useState<'decline' | 'limited-experience'>();
  const isMobile = useResponsiveValue(true, false);
  const shouldShowBidButton = offer.is_counter_offer;

  const { hasLimitedExperienceShipper, experienceList } =
    useShipperListHasLimitedExperience(
      isNewShipperAlertEnabled ? [offer.load.shipper_guid] : [],
    );

  const acceptOffer = useAcceptOffer({
    onSuccess: () => {
      trackOffersEvent({ name: 'Accepted Load Offer', guid: offer.guid });
      emitLoadEvent('accepted');
    },
  });

  useErrorSnackbar(acceptOffer.error, { onClose: acceptOffer.reset });

  function handleAcceptOffer() {
    if (hasLimitedExperienceShipper) {
      setDialog('limited-experience');
      return;
    }

    acceptOffer.mutate(offer.guid);
  }

  const isSubhaulingOffer = offer.is_subhauling_offer && isSubahulEnabled;

  return (
    <>
      <OffersDeclineDialog
        offer={offer}
        open={dialog === 'decline'}
        onSuccess={() => {
          setDialog(undefined);
        }}
        onClose={() => {
          setDialog(undefined);
        }}
      />

      {experienceList && (
        <LimitedExperienceDialog
          shippersExperience={experienceList}
          action="accept_offer"
          isOpen={dialog === 'limited-experience'}
          onCancel={() => {
            setDialog(undefined);
          }}
          onConfirm={() => {
            setDialog(undefined);
            acceptOffer.mutate(offer.guid);
          }}
        />
      )}

      <Box
        width="100%"
        marginLeft="large"
        marginRight="large"
        paddingTop="medium"
        paddingBottom="medium"
      >
        {isMobile ? (
          <Stack space="small">
            {shouldShowBidButton && (
              <PlaceBidButton offer={offer} disabled={acceptOffer.isPending} />
            )}

            <Columns space="small">
              <Column width="1/2">
                <PermissionAction name="marketplace" permission="can_manage">
                  <Button
                    size="medium"
                    variant={isSubhaulingOffer ? 'critical' : 'neutral'}
                    fullWidth={true}
                    startIcon={
                      <PermissionIcon
                        name="marketplace"
                        permission="can_manage"
                      />
                    }
                    disabled={acceptOffer.isPending}
                    onClick={() => {
                      setDialog('decline');
                    }}
                  >
                    Decline
                  </Button>
                </PermissionAction>
              </Column>
              <Column width="1/2">
                <PermissionAction name="marketplace" permission="can_manage">
                  <Button
                    size="medium"
                    variant="primary"
                    fullWidth={true}
                    startIcon={
                      <PermissionIcon
                        name="marketplace"
                        permission="can_manage"
                      />
                    }
                    pending={acceptOffer.isPending}
                    onClick={handleAcceptOffer}
                  >
                    Accept
                  </Button>
                </PermissionAction>
              </Column>
            </Columns>
          </Stack>
        ) : (
          <Columns space="small">
            <Column width={shouldShowBidButton ? 'fluid' : '1/2'}>
              <Box
                display="flex"
                justifyContent={shouldShowBidButton ? 'flex-start' : 'flex-end'}
              >
                <PermissionAction name="marketplace" permission="can_manage">
                  <Button
                    size="large"
                    variant={isSubhaulingOffer ? 'critical' : 'neutral'}
                    disabled={acceptOffer.isPending}
                    onClick={() => {
                      setDialog('decline');
                    }}
                    startIcon={
                      <PermissionIcon
                        name="marketplace"
                        permission="can_manage"
                      />
                    }
                  >
                    Decline
                  </Button>
                </PermissionAction>
              </Box>
            </Column>

            {shouldShowBidButton && (
              <Column width="content">
                <PlaceBidButton
                  offer={offer}
                  disabled={acceptOffer.isPending}
                />
              </Column>
            )}

            <Column width="content">
              <PermissionAction name="marketplace" permission="can_manage">
                <Button
                  size="large"
                  variant="primary"
                  startIcon={
                    <PermissionIcon
                      name="marketplace"
                      permission="can_manage"
                    />
                  }
                  pending={acceptOffer.isPending}
                  onClick={handleAcceptOffer}
                >
                  Accept
                </Button>
              </PermissionAction>
            </Column>
          </Columns>
        )}
      </Box>
    </>
  );
}
